import Activities from '@atom-web/icons-24/Activities'
import CheckBoxActive from '@atom-web/icons-24/CheckBoxActive'
import CheckBoxBlank from '@atom-web/icons-24/CheckBoxBlank'
import Flight from '@atom-web/icons-24/Flight'
import Hotel from '@atom-web/icons-24/Hotel'
import FireFill from '@atom-web/icons-24/jsx/FireFill'
import Star from '@atom-web/icons-24/jsx/Star'
import Visa from '@atom-web/icons-24/Visa'
import { Dialog, Drawer } from '@atom-web/macros'
import { Box, Button, Column, Flex, Row, Stack, Text } from '@atom-web/micros'
import dayjs from 'dayjs'
import { useAmp } from 'next/amp'
import { useRouter } from 'next/dist/client/router'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Link from 'next/link'
import queryString from 'query-string'
import {
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useReducer,
	useState,
} from 'react'

import { AppConfig } from '../../app-config'
import CbrWrapper from '../../components/CbrWrapper'
import CustomCheckbox from '../../components/CheckboxGroup'
import FloatingCTA from '../../components/FloatingCTA'
import Portal from '../../components/HOC/Portal'
import Card from '../../components/maldives_card_components/card'
import {
	budgetFilter,
	durationFilter,
	starFilter,
} from '../../data/vacations/filters'
import { useDidUpdate } from '../../hooks/useDidUpdate'
import Ribbonexample from '../../pages/maldives-tour-packages/ribbonexample'
import AppContext from '../../store/AppContext'
import capitializeFirstChar from '../../utils/capitializeFirstChar'
import clearImagePath from '../../utils/clearImagePath'
import usePrevious from '../../utils/customHooks/usePrevious'
import { decodeHotelRoomConfig } from '../../utils/decodeHotelRoomConfig'
import getAlternateTags from '../../utils/getAlternateTags'
import getImgIXUrl from '../../utils/getImgIXUrl'
import getComponents from '../../utils/getPageDataComponents'
import isIxigoPartnersSite from '../../utils/isIxigoPartnersSite'
import getLocale from '../../utils/locale-helpers/getLocale'
import get from '../../utils/lodashUtils/get'
import isEmpty from '../../utils/lodashUtils/isEmpty'
import {
	EVENT_CREATE_NOW_CLICKED,
	EVENT_Filter_CLICKED,
	EVENT_NEED_HELP_CLICKED,
	EVENT_PACKAGE_ITINERARY_CLICKED,
	EVENT_PACKAGE_SELECTED,
	EVENT_SB_CARD_SELECTED,
	EVENT_VACATION_CARD_SELECTED,
	EVENT_VIEW_ALL_PACKAGES_CLICKED,
	trackEvent,
} from '../../utils/ML/EventsTracker'
import redirector from '../../utils/redirector'
import { getPackagesV1 } from '../packages/actions/getPackages'
// Modals
import { TalkToExpert } from '../packages/components/TalkToExpert'
import GCMContainer from '../packages/gcm/GCMContainer'
import getCanonicalThemeUrl from '../packages/getCanonicalThemeUrl'
import getPackageRequestObject from '../packages/getPackageRequestObject'
import createMaldivesItinerary from '../tour-packages/actions/createMaldivesItinerary'
import pageLayout from './pageLayout'

// Dynamic imports
const PackagesCardsList = dynamic(() => import('../packages/cards'))
const CustomerTestimonials = dynamic(() =>
	import('../packages/components/CustomerTestimonials'),
)
const PackagesFAQ = dynamic(() => import('../packages/components/faq'))
const InterlinkingCards = dynamic(() =>
	import('../packages/components/InterlinkingCards'),
)
const SectionWithTitleWrapper = dynamic(() =>
	import('../packages/components/SectionWithTitleWrapper'),
)
const SEOInfoCard = dynamic(() => import('../packages/components/seoInfoCard'))
const CBRFloatingBar = dynamic(() =>
	import('../tour-packages/components/cbr-floating-bar'),
)
const AboutDestinationBlocks = dynamic(() =>
	import('../../components/AboutDestinationBlocks'),
)
const BankOfferCard = dynamic(() => import('../../components/BankOfferCard'))
const ContentWrapper = dynamic(() => import('../../components/ContentWrapper'))
const DesktopFilter = dynamic(() => import('../../components/DesktopFilter'))
const MobileFilter = dynamic(() => import('../../components/MobileFilter'))
const PackagesCard = dynamic(() => import('../../components/PackagesCard'))

const PackagesNewCard = dynamic(() =>
	import('../../components/PackagesNewCard'),
)

const PillList = dynamic(() => import('../../components/PillList'))
// const ValuePropBar = dynamic(() => import('../../components/ValuePropBar'))

const PackageBanner = dynamic(() => import('../packages/banner'))
const PackagesBannerFooter = dynamic(() =>
	import('../packages/components/banner-footer'),
)
const MediaComponents = dynamic(() =>
	import('../packages/components/MediaComponents'),
)

//https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85#gistcomment-2911761
const useIsomorphicLayoutEffect =
	typeof window !== 'undefined' ? useLayoutEffect : useEffect

const islandActivityData = [
	[
		'Lounge in the lap of luxury in your overwater villa',
		'Explore pristine beaches with your loved one in absolute privacy',
		'Witness the vibrant sea life in the turquoise depths of the Indian Ocean',
		'Avail exclusive honeymoon offers during your stay',
	],
	[
		'Stay in exceptional overwater villas and luxury resorts with chic modern amenities',
		'Enjoy an exquisite spread of meals included in your itinerary',
		'Sunbathe in the charming shores of sun-kissed beaches',
		'Avail exclusive honeymoon offers during your stay',
	],
	[
		'Relax in private infinity pools stretching into the turquoise Indian Ocean',
		'Snorkel in the tranquil blue sea and discover the vibrant marine life',
		'Indulge in the delicious local cuisine with alluring views of the ocean',
		'Avail exclusive honeymoon offers during your stay',
	],
	[
		'Experience luxury at its finest in overwater villas with private pools and sundecks',
		'Watch a romantic sunrise across the Indian Ocean with your loved one',
		'Build sand castles with your family on idyllic beaches',
		'Avail exclusive honeymoon offers during your stay',
	],
	[
		'Dine in the luxury of your private infinity pool with your loved one',
		'Relax and rejuvenate in the unspoilt beaches of the Indian Ocean',
		'Go on a romantic walk along the white-sand beach under the sensual moonlight',
		'Avail exclusive honeymoon offers during your stay ',
	],
]

function VacationsListingPage({
	data,
	sbPackages,
	isMobileDevice,
	createdTime,
	parsedCustomData,
	showBannerImage,
	slug,
}) {
	const router = useRouter()
	const isAmp = useAmp()

	const [packages, setPackages] = useState(data)
	const [showCBR, setShowCBR] = useState(false)
	const [isMobile, setMobile] = useState(isMobileDevice)
	const [selectedItinerary, setSelectedItinerary] = useState({
		event: null,
		itinerary: null,
	})

	const [filter, setFilter] = useState({
		show: false,
		key: '',
	})
	// eslint-disable-next-line no-unused-vars
	const [lastApplied, setLastApplied] = useState('')
	const [readMoreOpened, setReadModeOpened] = useState(false)
	const [{ userDetails }] = useContext(AppContext)
	const isIxigoPartnersSiteValue = isIxigoPartnersSite()

	const [filterValues, setFilterValue] = useReducer((state, diff) => {
		return {
			...state,
			...diff,
		}
	}, {})
	const [scrollPosition, setScrollPosition] = useState(false)
	const isPaid = useMemo(
		() => get(packages, 'campaignDetails.paid'),
		[packages],
	)

	useIsomorphicLayoutEffect(() => {
		if (window) {
			if (window.innerWidth <= 991) {
				setMobile(true)
			} else {
				setMobile(false)
			}
		}
	}, [])

	useEffect(() => {
		const viewPortHeight = window.visualViewport?.height || 0

		// Banner component
		const bannerEle = document.querySelector('.home-search-button')

		// Footer wrapper element
		const footerEle = document.getElementById('footer-wrapper')

		const scroll = () => {
			const scroll = window.scrollY
			const bannerSearchTopPos = bannerEle?.getBoundingClientRect().top || 0
			const footerTop = footerEle?.getBoundingClientRect().top || 4000

			setScrollPosition(scroll > bannerSearchTopPos + 50)
			if (footerTop < viewPortHeight) {
				setScrollPosition(false)
			}
		}

		window.addEventListener('scroll', scroll, {
			capture: true,
			passive: true,
		})
		return () =>
			window.removeEventListener('scroll', scroll, {
				capture: true,
			})
	}, [])

	useEffect(() => {
		setPackages(data)

		console.log({ sbPackages })
	}, [data])
	const localeFromUrl = get(packages, 'locale')
	const filteredItineraries = get(packages, 'filteredItineraries')
	const campaignDetails = get(packages, 'campaignDetails')
	const displayCurrency = get(packages, 'displayCurrency')
	const testimonials = get(packages, 'testimonials')

	const pageTitle = get(campaignDetails, 'name')
	const regionCode = get(campaignDetails, 'regionCode')
	const supportedLocales = get(campaignDetails, 'locales', [])
	const region = get(campaignDetails, 'region')
	const faqDetails = get(campaignDetails, 'faqDetails')
	const interlinkingBlocks = get(campaignDetails, 'interlinkingBlocks')
	const bannerPath = get(campaignDetails, 'image')
	const bannerText = get(campaignDetails, 'bannerText')

	// banner bottom
	const idealDuration = get(campaignDetails, 'idealDuration')
	const bestTimeToVisit = get(campaignDetails, 'bestTimeToVisit')
	const visaProcessingDays = get(campaignDetails, 'visaProcessingDays')
	const tripStartingPrice = get(packages, 'tripStartingPrice')
	const avgCHRating = get(campaignDetails, 'avgCHRating')
	const totalNumberOfCHRatings = get(campaignDetails, 'totalNumberOfCHRatings')

	const packagesTemplate = useMemo(
		() => get(packages, 'campaignDetails.template'),
		[packages],
	)
	const packagesBannerTemplate = useMemo(
		() => get(packages, 'campaignDetails.bannerTemplate'),
		[packages],
	)

	const bannerVideo = useMemo(
		() => parsedCustomData?.data?.bannerVideo || '',
		[parsedCustomData],
	)

	const packagesNewTheme = useMemo(() => {
		return packagesTemplate === 'PAID_CARD'
	}, [packagesTemplate])
	// INTERLINKING-Blocks
	const parsedLinksForPills = useMemo(() => {
		let themeBasedDestinations = []
		let departureCityDestinations = []
		let cityBasedDestinations = []
		let otherGroupDestinations = []

		// Destination links for pills
		const destinationPills = interlinkingBlocks?.map(desti => {
			if (desti?.categoryName === 'THEME') {
				themeBasedDestinations = desti
			} else if (desti?.categoryName === 'DEPARTURE_CITY') {
				departureCityDestinations = desti
			} else if (desti?.categoryName === 'INTERLINKED_CITY') {
				cityBasedDestinations = desti
			} else {
				otherGroupDestinations = [...otherGroupDestinations, { ...desti }]
			}

			const destinations = desti?.interlinkedPageDetails?.map(item => {
				return {
					displayText: item?.name,
					slug: item?.key,
				}
			})

			return {
				categoryName: desti.categoryName,
				category: desti.category,
				destinations,
			}
		})

		return {
			themeBasedDestinations,
			departureCityDestinations,
			cityBasedDestinations,
			otherGroupDestinations,
			destinationPills,
		}
	}, [interlinkingBlocks])

	const getThemeByUrl = () => {
		const pathname = router.asPath
		// Current Available themes for package page
		const themeValue = [
			'honeymoon',
			'family',
			'beach',
			'adventure',
			'visa-on-arrival',
		]

		/**
		 * Getting the theme value from the given URL as we are using Dynamic Routing
		 * Here we have splited and retireved the theme value alone
		 * 1) Retrieved the whole URL, splitted and retrieved the 0th value i.e. (honeymoon-packages)
		 * 2) Again splitted and retrieved the theme value i.e(honeymoon)
		 */
		let theme = pathname.split('/')[1].split('-')[0]

		/**
		 * Getting the theme value form the given URL.
		 * for visa-on-arrival while splitting with '-' we get visa. so based on that provided the text manually
		 */
		theme = theme === 'visa' ? 'visa-on-arrival' : theme

		/**
		 * setting empty value if there is no theme value.
		 */
		if (!themeValue.includes(theme)) {
			theme = ''
		}

		return theme
	}

	const onCloseModal = e => {
		setShowCBR(e)
	}

	const theme = getThemeByUrl()

	// SEO-META
	let canonicalTag = ''
	const isCanonical = get(campaignDetails, 'canonicalUrl') || false
	const locale = getLocale({ router, locale: localeFromUrl })
	const isGlobalUser = useMemo(() => locale && locale !== 'in', [locale])

	if (isCanonical) {
		canonicalTag = (
			<link
				rel="canonical"
				href={`${AppConfig.domain_url}${
					locale ? `${locale}/` : ''
				}${isCanonical}`}
			/>
		)
	} else {
		canonicalTag = getCanonicalThemeUrl({
			theme,
			pathname: router.asPath,
			isFromPackages: true,
			locale: locale,
		})
	}

	const metaTitle = get(
		campaignDetails,
		'seoDetails.metaTitle',
		'pickyourtrail.com',
	)
	const metaDescription = get(
		campaignDetails,
		'seoDetails.metaDescription',
		'Pickyourtrail is the right platform to plan your vacation',
	)
	const metaKeywords = get(
		campaignDetails,
		'seoDetails.metaKeywords',
		'Pickyourtrail, vacation',
	)

	// Disabling leadGenForm flow for all regions
	const showLeadGenForm = false

	// const { asPath } = router
	// const path = asPath?.toLowerCase() || ''
	// const showLeadGenForm =
	// 	isAmp || locale
	// 		? false
	// 		: path.includes('australia') && path.includes('packages')

	const scrollToPackages = () => {
		trackEvent(EVENT_VIEW_ALL_PACKAGES_CLICKED, {
			Region: region,
		})
		const target = '#packages-cards'
		var element = document?.querySelector(target)
		var headerOffset = isMobile ? 10 : 65
		var elementPosition = element?.getBoundingClientRect().top
		var offsetPosition = elementPosition + window.pageYOffset - headerOffset

		window.scrollTo({
			top: offsetPosition,
			behavior: 'smooth',
		})
	}

	const openCBR = () => setShowCBR(true)
	const closeCBR = () => setShowCBR(false)

	const cardClickEvents = itinerary => {
		trackEvent(EVENT_VACATION_CARD_SELECTED, {
			title: itinerary.title,
			cost: itinerary.itineraryCost,
			is_card_clicked: true,
			element_name: 'card_vacation',
			element_type: 'card',
			path: router.asPath,
			version: 2.0,
		})
	}

	const showGetCostModal = data => {
		const { event, itinerary } = data || selectedItinerary
		if (locale) {
			event.stopPropagation()
			trackEvent(EVENT_PACKAGE_SELECTED, {
				title: itinerary.title,
				cost: itinerary.itineraryCost,
				is_card_clicked: false,
				version: 2.0,
			})
			const link = `${locale ? `/${locale}` : ''}/${itinerary.slug}`
			window.location.href = link
		} else {
			event.preventDefault()

			if (router.query.m !== 'gcm') {
				cardClickEvents(itinerary)
				const itineraryId = itinerary.campaignItineraryId
				const query = {
					m: 'gcm',
					itineraryId,
					n: itinerary?.nights,
					rc: itinerary?.regionCode,
					name: itinerary?.title,
					rn: itinerary?.regionName,
				}

				router.replace(
					{
						pathname: router.asPath.split('?')[0],
						query,
					},
					null,
					{ shallow: true, locale, scroll: false },
				)
			}
		}
	}

	const onTitleClick = (e, itinerary) => {
		e.stopPropagation()
		trackEvent(EVENT_PACKAGE_SELECTED, {
			title: itinerary.title,
			cost: itinerary.itineraryCost,
			is_card_clicked: false,
			version: 2.0,
		})
		if (showLeadGenForm) showGetCostModal({ event: e, itinerary })
	}

	const onCardClick = (
		event,
		itinerary,
		title,
		itineraryCost,
		campaignItineraryId,
	) => {
		event && event.stopPropagation()
		trackEvent(EVENT_PACKAGE_ITINERARY_CLICKED, {
			Itinerary_name: title,
			cost: itineraryCost,
			itineraryId: campaignItineraryId,
			Region: region,
		})
		if (showLeadGenForm && !userDetails?.loggedIn) {
			setSelectedItinerary({ event, itinerary })
			cardClickEvents(itinerary)
			openCBR()
		} else {
			showGetCostModal({ event, itinerary })
		}
	}

	const onUnlockDeal = (
		resortCode,
		resortName,
		recommended,
		title,
		itineraryCost,
		campaignItineraryId,
	) => {
		const requestData = {
			resortCode,
			departureAirportCode: '$$$',
			arrivalAirportCode: '$$$',
			departureDate: dayjs().add(45, 'day').format('YYYY-MM-DD'),
			passengerConfiguration: decodeHotelRoomConfig('a2'),
			nights: 4,
			nationality:
				typeof query.nationality === 'string'
					? query.nationality.toUpperCase() || 'IN'
					: 'IN',
			onwardArrivalAirportCode: 'MLE',
			returnDepartureAirportCode: 'MLE',
			topHotelsOnly: true,
		}
		trackEvent(EVENT_PACKAGE_ITINERARY_CLICKED, {
			Itinerary_name: title,
			cost: itineraryCost,
			itineraryId: campaignItineraryId,
			Region: region,
		})
		trackEvent(EVENT_SB_CARD_SELECTED, {
			resort_id: resortCode,
			resort_name: resortName,
			departure_date: requestData?.departureDate,
			departure_city: requestData?.departureAirportCode,
			element_name: 'btn_packages_listing_page_check_availability',
			nationality: requestData?.nationality,
			recommended,
		})

		if (userDetails && userDetails.loggedIn) {
			console.log({ requestData, userDetails })

			createMaldivesItinerary(
				requestData,
				itineraryId => {
					redirector(`/customize/mle/inclusions/${itineraryId}`)
				},
				() => {
					// setShowError(true)
				},
				router,
				{},
				locale,
			)
		} else {
			const q = router.query
			q.m = isGlobalUser ? 'auth' : 'gcm'
			q.name = resortName
			q.depCity = requestData?.departureAirportCode?.from
			q.resortCode = resortCode
			q.sbPackages = 1
			q.rn = 'Maldives'
			q.rc = 'mle'
			q.date = dayjs().add(45, 'day').format('YYYY-MM-DD')
			q.travellers = 'a2'
			q.from = '$$$'
			q.arrivalAirportCode = '$$$'
			q.duration = 4

			router.push({ pathname: router.asPath.split('?')[0], query: q }, null, {
				shallow: true,
			})
		}
	}

	const pageTitleCap = capitializeFirstChar(pageTitle)

	const leadGenForm = (
		<TalkToExpert
			onClose={closeCBR}
			vacationFlow={showLeadGenForm}
			ctaText="Get best quotes"
			title={pageTitleCap}
			regionCode={regionCode}
			isMobile={isMobile}
			formVisible={showCBR}
			scrollToPackagesList={scrollToPackages}
			callback={selectedItinerary?.itinerary ? showGetCostModal : null}
		/>
	)

	const arrayToRepeat = parseInt(
		Math.ceil(
			(sbPackages?.resortCostingDetails || []).length /
				(islandActivityData || []).length,
		) - 1,
	)
	/** used fill() for looping purpose*/
	const newIslandActivityData = Array(arrayToRepeat > 0 ? arrayToRepeat : 1)
		.fill()
		.reduce(acc => acc.concat(acc), islandActivityData)

	// const showSbCards = useMemo(
	// 	() => ['honeymoon-packages/maldives', 'packages/maldives'].includes(slug),
	// 	[slug],
	// )
	const showSbCards = false

	const getItineraries = ({ start = 0, count = 3 }) => {
		const itineraries = [...(filteredItineraries || [])].splice(start, count)
		if (showSbCards && !isGlobalUser) {
			const cardsList = [...(sbPackages.resortCostingDetails || [])].splice(
				start,
				count,
			)
			return (
				<Column
					css={{
						width: '100%',
					}}
				>
					{cardsList?.length
						? cardsList?.map((item, index) => {
								return (
									<Card
										isIxigoPartnersSite={isIxigoPartnersSiteValue}
										key={'resort-card' + index}
										{...item}
										displayCurrency={displayCurrency}
										isMobile={isMobile}
										onPress={onUnlockDeal}
										activities={newIslandActivityData[index]}
										ind={index}
										packageListingPage={true}
									/>
								)
						  })
						: null}
				</Column>
			)
		} else if (packagesTemplate === 'PACKAGE_V1') {
			return (
				<Column
					css={{
						width: '100%',
					}}
				>
					<PackagesCardsList
						paginatedItineraries={itineraries || []}
						regionCode={regionCode}
						testimonials={testimonials}
						isMobileDevice={isMobile}
						displayCurrency={displayCurrency}
						localeFromUrl={localeFromUrl}
						isPaid={false}
						showLeadGenForm={showLeadGenForm}
						onCardClick={onCardClick}
						startIndex={!showBannerImage && start}
						region={region}
					/>
				</Column>
			)
		} else if (packagesTemplate === 'PAID_CARD') {
			//packagesTemplate === 'PAID_CARD'
			const ribbonText = text => {
				return text?.filter(
					val =>
						val == 'PYT Exclusive' ||
						val == 'Honeymoon special' ||
						val == 'Pocket friendly' ||
						val == 'SUPER_SAVER' ||
						val == 'PYT_CHOICE',
				)
			}
			return itineraries?.map((item, inx) => {
				const theme = item['uiThemeByKey'][slug] === 'dark' ? true : false
				return (
					<Box
						key={`pack-card-v3-${inx}`}
						css={{
							width: '100%',
							position: 'relative',
							display: 'flex',
							justifyContent: 'center',
							'@lg': {
								width: 'calc(50% - 24px)',
							},
							'@xl': {
								width: 'calc(33.33% - 22px)',
							},
						}}
					>
						<PackagesNewCard
							data={item}
							theme={theme}
							onCardClick={e => onCardClick(e, item)}
							displayCurrency={displayCurrency}
							onTitleClick={
								(showLeadGenForm ? false : !isPaid)
									? e => onTitleClick(e, item)
									: null
							}
							trustCard={
								start === 0 && inx === 0
									? { ...parsedCustomData?.data?.trustCards?.[0] }
									: start === 0 && inx === 1
									? { ...parsedCustomData?.data?.trustCards?.[1] }
									: start === 0 && inx === 2
									? { ...parsedCustomData?.data?.trustCards?.[2] }
									: {}
							}
							offerValue={item?.dealDiscountPercentage}
						/>
						{ribbonText(item?.pills)?.length ? (
							<Box
								css={{
									position: 'absolute',
									top: '16px',
									left: '-16px',
									zIndex: 2,
								}}
							>
								<Ribbonexample
									// text={theme ? 'PYT CHOICE' : 'SUPER SAVER'}
									text={ribbonText(item?.pills)}
									textColor={theme ? '$grey12' : '$white'}
									fontSize={'12px'}
									backgroundColor={theme ? '#f8cb4b' : '#EF435D'}
									icon={
										theme ? (
											<Star
												fill="#333333"
												css={{
													height: 16,
													width: 16,
													marginRight: '5px',
												}}
											/>
										) : (
											<FireFill
												css={{
													height: 18,
													width: 18,
													fill: '#FFFFFF',
													marginRight: '5px',
												}}
											/>
										)
									}
								/>
							</Box>
						) : null}
					</Box>
				)
			})
		} else {
			return itineraries.map((item, inx) => {
				return (
					<Box
						key={`pack-card-${inx}`}
						css={{
							width: '100%',
							'@lg': {
								width: 'calc(50% - 24px)',
							},
							'@xl': {
								width: 'calc(33.33% - 22px)',
							},
						}}
					>
						<PackagesCard
							{...item}
							isMobile={isMobile}
							displayCurrency={displayCurrency}
							index={inx}
							onCardClick={e => onCardClick(e, item)}
							headerAsLink={showLeadGenForm ? false : !isPaid}
							headerLink={
								isPaid ? null : `${locale ? `/${locale}` : ''}/${item.slug}`
							}
							onTitleClick={
								(showLeadGenForm ? false : !isPaid)
									? e => onTitleClick(e, item)
									: null
							}
							loadImageOnPriority={
								!showBannerImage && isPaid && start === 0 && inx === 0
							}
							trustCard={
								start === 0 && inx === 0
									? { ...parsedCustomData?.data?.trustCards?.[0] }
									: start === 0 && inx === 1
									? { ...parsedCustomData?.data?.trustCards?.[1] }
									: start === 0 && inx === 2
									? { ...parsedCustomData?.data?.trustCards?.[2] }
									: {}
							}
						/>
					</Box>
				)
			})
		}
	}

	const handleFilter = () => {
		const filters = {}
		setLastApplied(filter.key)
		const filterKeys = Object.keys(filterValues)
		if (
			filterKeys.includes('hotelRatings') &&
			filterValues['hotelRatings']?.length
		) {
			filters.hotelRatings = filterValues['hotelRatings']
		}
		if (filterKeys.includes('durations') && filterValues['durations']?.length) {
			filters.durations = filterValues['durations']
		}
		if (filterKeys.includes('budgets') && filterValues['budgets']?.length) {
			filters.budgets = filterValues['budgets']
		}
		const withoutQuery = router.asPath?.split('?')[0]
		const routerPath = `${locale ? `/${locale}` : ''}${withoutQuery}${
			queryString.stringify(filters) ? `?${queryString.stringify(filters)}` : ''
		}`
		router.replace(routerPath, null, { shallow: true })
		setFilter({
			show: false,
			key: '',
		})
	}

	const { query } = router
	const prevQuery = usePrevious(query)

	useDidUpdate(() => {
		const filters = {}
		if (!isEmpty(query?.budgets)) {
			filters.budgets =
				typeof query?.budgets === 'string'
					? [query.budgets]
					: [...query.budgets]
		}
		if (!isEmpty(query?.durations)) {
			filters.durations =
				typeof query.durations === 'string'
					? [query.durations]
					: [...query.durations]
		}
		if (!isEmpty(query?.hotelRatings)) {
			filters.hotelRatings =
				typeof query.hotelRatings === 'string'
					? [query.hotelRatings]
					: [...query.hotelRatings]
		}
		setFilterValue(filters)

		if (
			prevQuery.budgets !== query.budgets ||
			prevQuery.durations !== query.durations ||
			prevQuery.hotelRatings !== query.hotelRatings
		) {
			const key = query?.slug?.join('/')
			const reqObj = getPackageRequestObject({}, key, filters)

			getPackagesV1(reqObj, locale).then(res => {
				setPackages(res)
			})
		}
	}, [query])

	const handleCloseFilter = e => {
		setFilter({
			show: e,
			key: '',
		})
		const filters = {}
		if (!isEmpty(query?.budgets)) {
			filters.budgets =
				typeof query.budgets === 'string' ? [query.budgets] : [...query.budgets]
		}
		if (!isEmpty(query?.durations)) {
			filters.durations =
				typeof query.durations === 'string'
					? [query.durations]
					: [...query.durations]
		}
		if (!isEmpty(query?.hotelRatings)) {
			filters.hotelRatings =
				typeof query.hotelRatings === 'string'
					? [query.hotelRatings]
					: [...query.hotelRatings]
		}

		!filters?.budgets ? (filters.budgets = []) : null
		!filters?.durations ? (filters.durations = []) : null
		!filters?.hotelRatings ? (filters.hotelRatings = []) : null
		setFilterValue(filters)
	}

	const filters = useMemo(
		() => [...durationFilter, ...starFilter, ...budgetFilter],
		[],
	)

	const clearAllFilters = () => {
		setFilterValue({
			budgets: [],
			durations: [],
			hotelRatings: [],
		})
	}

	const clearThisFilter = () => {
		setFilterValue({
			[filter.key]: [],
		})
	}

	const getPackageComponents = ({ component, config }) => {
		switch (component) {
			case 'valueProps':
				return null
			// return (
			// 	<ValuePropBar
			// 		items={
			// 			parsedCustomData?.data?.[config?.source] || [
			// 				{
			// 					icon: 'Baggage',
			// 					title: '20000+ travellers',
			// 				},
			// 				{
			// 					icon: 'Global',
			// 					title: '37 global destinations',
			// 				},
			// 				{
			// 					icon: 'Heart',
			// 					title: "India's loved travel brand",
			// 				},
			// 				{
			// 					icon: 'Viewdetail',
			// 					title: '1m+ curated itineraries',
			// 				},
			// 				{
			// 					icon: 'Instagram',
			// 					title: '150k+ insta followers',
			// 				},
			// 				{
			// 					icon: 'Google',
			// 					title: '4.7/5 Google rating',
			// 				},
			// 			]
			// 		}
			// 		isMobile={isMobile}
			// 	/>
			// )
			case 'seoInfoBlock':
				if (isPaid) return null

				return (
					<Column>
						<div id="seo-infor-block">
							<SEOInfoCard
								packages={packages}
								region={region}
								theme={theme}
								isMobile={isMobile}
								openState={readMoreOpened}
								onToggle={setReadModeOpened}
							/>
						</div>
					</Column>
				)
			case 'packageCards':
				return (
					<div id={config?.start === 0 ? `packages-cards` : ''}>
						{(filteredItineraries || [])?.length === 0 &&
						config?.start === 0 ? (
							/* No content section */
							<Column
								css={{
									paddingBottom: 60,
									backgroundColor: isIxigoPartnersSiteValue
										? '#ffefea'
										: '#F1F8F6',
									'@lg': {
										paddingBottom: 120,
									},
								}}
							>
								<Column
									css={{
										paddingTop: 68,
										width: '100%',
										justifyContent: 'center',
										alignItems: 'center',
									}}
								>
									<Row
										css={{
											gap: 10,
											'@lg': {
												paddingTop: 40,
											},
										}}
									>
										<Column
											css={{
												gap: 8,
											}}
										>
											<Hotel
												width={32}
												height={32}
												fill={isIxigoPartnersSiteValue ? '#FF8C68' : '#A0CFC2'}
											/>
											<Flight
												width={32}
												height={32}
												fill={isIxigoPartnersSiteValue ? '#FF8C68' : '#A0CFC2'}
											/>
										</Column>
										<Column
											css={{
												gap: 8,
												marginTop: 20,
											}}
										>
											<Activities
												width={32}
												height={32}
												fill={isIxigoPartnersSiteValue ? '#FF8C68' : '#A0CFC2'}
											/>
											<Visa
												width={32}
												height={32}
												fill={isIxigoPartnersSiteValue ? '#FF8C68' : '#A0CFC2'}
											/>
										</Column>
									</Row>
									<Text
										css={{
											margin: 0,
											fontSize: 20,
											fontWeight: 400,
											lineHeight: '28px',
											color: '#333',
											textAlign: 'center',
										}}
									>
										Oh no! We couldn't find itineraries matching your interests!
									</Text>
									<Text
										css={{
											margin: 'auto',
											fontSize: 20,
											fontWeight: 400,
											lineHeight: '28px',
											color: '#333',
											maxWidth: '80%',
											textAlign: 'center',
											'@sm': {
												maxWidth: '100%',
											},
										}}
									>
										Fret not, our Travel Consultants are here to help you out!
									</Text>
									<Button
										primary
										css={{
											// background:
											// 	'linear-gradient(94.77deg, #009E6A 0.56%, #00C684 100%)',
											marginTop: 16,
											marginBottom: 16,
										}}
										onClick={showCbr}
									>
										Talk to a Travel Consultants
									</Button>
									<Link href="/customize">
										<a>Create a personalised itinerary</a>
									</Link>
								</Column>
							</Column>
						) : null}

						<Stack
							gap={{ '@initial': '6', '@sm': '7' }}
							css={{
								width: '100%',
								'@sm': {
									display: config?.desktop ? 'none' : 'flex',
								},
							}}
						>
							{getItineraries({ start: config?.start, count: config?.count })}
						</Stack>
						{config?.desktop && (
							<Stack
								gap={'7'}
								css={{
									width: '100%',
									display: 'none',
									'@sm': {
										display: 'flex',
									},
								}}
							>
								{getItineraries({
									start: config?.desktop?.start,
									count: config?.desktop?.count,
								})}
							</Stack>
						)}
					</div>
				)
			case 'themedDestinationInterlinkingCards':
				return !isPaid &&
					!showSbCards &&
					parsedLinksForPills?.themeBasedDestinations?.interlinkedPageDetails
						?.length > 0 ? (
					<Column
						css={{
							width: '100%',
						}}
					>
						<SectionWithTitleWrapper
							isMobile={isMobile}
							title={`${capitializeFirstChar(
								packages?.campaignDetails?.key?.split('/')[1],
							)} Packages By Theme`}
						>
							<InterlinkingCards
								categoryType={
									parsedLinksForPills?.themeBasedDestinations?.categoryName
								}
								destinations={
									parsedLinksForPills?.themeBasedDestinations
										?.interlinkedPageDetails
								}
								isMobile={isMobile}
								displayCurrency={displayCurrency}
							/>
						</SectionWithTitleWrapper>
					</Column>
				) : null
			case 'mediaComponents':
				return !isPaid &&
					parsedCustomData?.data?.mediaComponents?.data?.length > 0 ? (
					<Column
						css={{
							width: '100%',
						}}
					>
						<SectionWithTitleWrapper
							isMobile={isMobile}
							title={`${capitializeFirstChar(
								parsedCustomData?.data?.mediaComponents?.title,
							)}`}
						>
							<MediaComponents
								data={parsedCustomData?.data?.mediaComponents?.data}
							/>
						</SectionWithTitleWrapper>
					</Column>
				) : null
			case 'offerCards':
				return parsedCustomData?.data?.[config.source] ? (
					<Row
						css={{
							width: '100%',
							height: 130,
							overflow: 'scroll',
							flexWrap: 'nowrap',
							gap: 14,
							'@sm': {
								height: 'unset',
								overflow: 'unset',
								flexWrap: 'wrap',
								gap: 32,
							},
						}}
					>
						{parsedCustomData?.data?.[config.source]?.map((val, inx) => (
							<BankOfferCard
								key={`offer-car-${inx}`}
								{...val}
								backgroundImage={getImgIXUrl(val?.backgroundImage)}
								isMobile={isMobile}
							/>
						))}
					</Row>
				) : null
			case 'departureCityInterlinkingCards':
				return !isPaid &&
					!showSbCards &&
					parsedLinksForPills?.departureCityDestinations?.interlinkedPageDetails
						?.length > 0 ? (
					<Column
						css={{
							width: '100%',
						}}
					>
						<SectionWithTitleWrapper
							isMobile={isMobile}
							title={`${capitializeFirstChar(
								region,
							)} Packages By Departure city`}
						>
							<InterlinkingCards
								categoryType={
									parsedLinksForPills?.departureCityDestinations?.categoryName
								}
								destinations={
									parsedLinksForPills?.departureCityDestinations
										?.interlinkedPageDetails
								}
								isMobile={isMobile}
								displayCurrency={displayCurrency}
							/>
						</SectionWithTitleWrapper>
					</Column>
				) : null
			case 'byCitiesInterlinkingCards':
				return !isPaid &&
					parsedLinksForPills?.cityBasedDestinations?.interlinkedPageDetails
						?.length > 0 ? (
					<Column
						css={{
							width: '100%',
						}}
					>
						<SectionWithTitleWrapper
							isMobile={isMobile}
							title={`${capitializeFirstChar(region)} Packages By Cities`}
						>
							<InterlinkingCards
								categoryType={
									parsedLinksForPills?.cityBasedDestinations?.categoryName
								}
								destinations={
									parsedLinksForPills?.cityBasedDestinations
										?.interlinkedPageDetails
								}
								isMobile={isMobile}
								displayCurrency={displayCurrency}
							/>
						</SectionWithTitleWrapper>
					</Column>
				) : null
			case 'aboutDestination':
				return parsedCustomData?.data?.[config.source] ? (
					<Column
						css={{
							width: '100%',
							minHeight: 300,
						}}
					>
						<AboutDestinationBlocks
							isMobile={isMobile}
							{...parsedCustomData?.data?.[config.source]}
						/>
					</Column>
				) : null
			case 'customerTestimonials':
				return (
					<CustomerTestimonials
						pageTitleCap={pageTitleCap}
						testimonials={testimonials}
						isMobile={isMobile}
						region={region}
					/>
				)
			case 'faq':
				return faqDetails?.length && !locale ? (
					<Column
						css={{
							width: '100%',
							'@sm': {
								width: 'calc(80% + 84px)',
							},
						}}
					>
						<PackagesFAQ faqDetails={faqDetails} isMobile={isMobile} />
					</Column>
				) : null
			case 'destinationPills':
				return (
					!isPaid &&
					parsedLinksForPills?.destinationPills?.map((item, inx) => {
						return (
							<Column
								css={{
									width: '100%',
								}}
								key={`interlinking-block-${inx}`}
							>
								<div id={`interlinking-pills-${item.categoryName}`}>
									<SectionWithTitleWrapper
										isMobile={isMobile}
										title={`${item?.category?.trim()}`}
									>
										<PillList content={item.destinations} isMobile={isMobile} />
									</SectionWithTitleWrapper>
								</div>
							</Column>
						)
					})
				)
			default:
				return getComponents({
					...config,
					...parsedCustomData?.data?.[config.source],
					mobileDevice: isMobile,
					region: region,
				})
		}
	}

	const checkCondition = useCallback(
		({ key, value }) => {
			if (filterValues[key]?.includes(value)) {
				return true
			}
			return false
		},
		[filterValues],
	)

	const onCheckboxClick = useCallback(
		e => {
			if (e.checked && !Object.keys(filterValues).includes(e.groupKey)) {
				setFilterValue({
					[e.groupKey]: [e.key],
				})
			} else if (
				e.checked &&
				Object.keys(filterValues).includes(e.groupKey) &&
				!filterValues[e.groupKey].includes(e.key)
			) {
				setFilterValue({
					[e.groupKey]: [...filterValues[e.groupKey], e.key],
				})
			} else if (
				!e.checked &&
				Object.keys(filterValues).includes(e.groupKey) &&
				filterValues[e.groupKey].includes(e.key)
			) {
				const filterCopy = [...filterValues[e.groupKey]]
				const index = filterCopy.indexOf(e.key)
				filterCopy.splice(index, 1)
				setFilterValue({
					[e.groupKey]: filterCopy,
				})
			} else if (
				!e.checked &&
				Object.keys(filterValues).includes(e.groupKey) &&
				!filterValues[e.groupKey]?.length
			) {
				const filterCopy = filterValues
				delete filterCopy[e.groupKey]
				setFilterValue(filterCopy)
			}
		},
		[filterValues],
	)

	const mobileFilterArray = useMemo(
		() =>
			filter.key === 'hotelRatings'
				? starFilter
				: filter.key === 'durations'
				? durationFilter
				: filter.key === 'budgets'
				? budgetFilter
				: [],
		[filter],
	)

	const { locale: countryLocale } = router

	const showCbr = () => {
		trackEvent(EVENT_NEED_HELP_CLICKED, {
			Region: region,
		})
		if (locale == 'in' || !locale) {
			// const query = {
			// 	m: 'cbr',
			// }
			router.replace(
				{
					query: { ...router.query, m: 'cbr' },
				},
				null,
				{ shallow: true, locale, scroll: false },
			)
		} else {
			console.log('global localee')
			setShowCBR(true)
		}
	}

	return (
		<Fragment>
			<Head>
				{canonicalTag}
				<title>{metaTitle}</title>
				<meta name="description" content={metaDescription} />
				<meta name="keywords" content={metaKeywords} />
				{getAlternateTags({ pathname: router.asPath, supportedLocales })}
				{isPaid && <meta name="robots" content="noindex, follow" />}
				<meta
					property="og:image"
					content={
						clearImagePath(bannerPath, `https://pyt-images.imgix.net/images`) ||
						null
					}
				/>
				<meta property="og:title" content={metaTitle || 'Pickyourtrail'} />
				<meta
					property="og:description"
					content={metaDescription || 'Pickyourtrail'}
				/>
				{countryLocale === 'in' ? (
					<meta
						property="og:url"
						// href={`${AppConfig.domain_url}${countryLocale}${canonicalTag?.props?.href}`}
						content={`${
							AppConfig.domain_url.endsWith('/')
								? AppConfig.domain_url.slice(0, -1)
								: AppConfig.domain_url
						}${router.asPath}`}
					/>
				) : (
					<meta
						property="og:url"
						// href={`${AppConfig.domain_url}${countryLocale}${canonicalTag?.props?.href}`}
						content={`${AppConfig.domain_url}${
							countryLocale !== 'in' && countryLocale
						}${router.asPath}`}
					/>
				)}
				<meta property="og:type" content="website" />
			</Head>
			<Box data-created-time={createdTime}>
				{
					// packagesTemplate === 'PACKAGE_V1' ? (
					<Box
						css={{
							backgroundColor: '$black',
							height: packagesNewTheme ? '76vh' : '80vh',
							'@smMax': {
								height: packagesNewTheme && '70vh',
							},
						}}
					>
						<PackageBanner
							imageUrl={clearImagePath(bannerPath, `images`)}
							page={'packages'}
							className="parallax"
							title={`${pageTitle}`}
							bannerTitle={pageTitle}
							bannerSubText={bannerText}
							isPaid={isPaid}
							onDownArrowClick={scrollToPackages}
							onGetBestPriceClick={openCBR}
							isMobile={isMobile}
							showLeadGenForm={showLeadGenForm}
							regionCode={regionCode}
							bannerVideo={bannerVideo}
							packagesNewTheme={packagesNewTheme}
							packagesBannerTemplate={packagesBannerTemplate}
							newBannerVideo={campaignDetails?.video}
						/>
						<ContentWrapper
							css={{
								'@lg': {
									bottom: packagesNewTheme && '5%',
								},
								bottom: packagesNewTheme && '10%',
							}}
						>
							<PackagesBannerFooter
								isPaid={isPaid}
								idealDuration={idealDuration}
								bestTimeToVisit={bestTimeToVisit}
								visaProcessingDays={visaProcessingDays}
								tripStartingPrice={tripStartingPrice}
								rating={avgCHRating}
								ratingsCount={totalNumberOfCHRatings}
								displayCurrency={displayCurrency}
								isMobile={isMobile}
								packagesNewTheme={packagesNewTheme}
							/>
						</ContentWrapper>
					</Box>
					// ) : (
					// 	<SimpleHeroCard
					// 		showBannerImage={showBannerImage}
					// 		pageTitle={pageTitleCap}
					// 		subTitle={bannerText}
					// 		isMobile={isMobile}
					// 		tourismLogo={parsedCustomData?.data?.tourismLogo}
					// 		pytLogo={parsedCustomData?.data?.pytLogo}
					// 		isPaid={isPaid}
					// 		banner={parsedCustomData?.data?.banner}
					// 	/>
					// )
				}

				<Flex
					css={{
						display: 'none',
						'@lg': {
							display: showSbCards ? 'none' : 'unset',
							width: '100%',
							marginTop: filteredItineraries?.length ? 0 : 72,
							justifyContent: 'center',
							position: 'relative',
						},
					}}
				>
					<DesktopFilter
						filterOptions={[
							{
								key: 'durations',
								name: 'No of Days',
								isApplied: filterValues['durations']?.length,
								icon: 'Calendar',
							},
							{
								key: 'hotelRatings',
								name: 'Hotel Ratings',
								isApplied: filterValues['hotelRatings']?.length,
								icon: 'StarOutline',
							},
							{
								key: 'budgets',
								name: 'Budget',
								isApplied: filterValues['budgets']?.length,
								icon: 'Wallet',
							},
						]}
						isPressed={filter}
						setIsPressed={setFilter}
						clickAction={showCbr}
					/>
				</Flex>

				<Box
					css={{
						backgroundColor: '#F8FCFB',
						width: '100%',
						paddingBottom: isPaid ? 64 : 0,
						'@sm': {
							paddingBottom: isPaid ? 32 : 0,
						},
					}}
				>
					<ContentWrapper
						padding="20px"
						css={{
							paddingTop: '24px',
							paddingBottom: '24px',
							backgroundColor: '#F7F7F7',
							'@lg': {
								backgroundColor: 'transparent',
							},
						}}
					>
						<>
							<Column
								css={{
									gap: '$6',
									marginBottom: '56px',
									'@sm': {
										gap: '$7',
									},
								}}
							>
								{(
									parsedCustomData?.pageLayout?.common || pageLayout.common
								).map(component => {
									return getPackageComponents({ ...component })
								})}
							</Column>
						</>

						{!isAmp && showLeadGenForm && !showSbCards ? (
							<Box
								css={{
									display: 'block',
									'@sm': {
										display: 'none',
									},
								}}
							>
								<CBRFloatingBar
									isMobile={isMobile}
									ctaText="Speak to Aussie Specialist"
									vactionFlow={true}
									leadGenForm={leadGenForm}
									showClose={false}
								/>
							</Box>
						) : null}

						{/* Mobile filter */}
						<Portal>
							<Box
								key="mobile-filter"
								css={{
									width: '100%',
									zIndex: 3,
									position: 'fixed',
									bottom: 0,
									boxShadow: '0px 4px 18px rgba(133, 194, 176, 0.06)',
									display: showSbCards ? 'none' : 'block',
									'@sm': {
										display: 'none',
									},
								}}
							>
								<MobileFilter
									filterOptions={[
										{
											key: 'durations',
											name: 'No of Days',
											icon: 'FireOutline',
											color: '#777777',
											isApplied: filterValues['durations']?.length,
										},
										{
											key: 'hotelRatings',
											name: 'Hotel Ratings',
											icon: 'StarOutline',
											color: '#777777',
											isApplied: filterValues['hotelRatings']?.length,
										},

										{
											key: 'budgets',
											name: 'Budget',
											icon: 'SavingMoney',
											color: '#777777',
											isApplied: filterValues['budgets']?.length,
										},
										{
											key: 'cbr',
											name: 'Talk to us',
											icon: 'ContactFill',
											color: '#777777',
											isApplied: false,
										},
									]}
									clickAction={showCbr}
									isPressed={filter}
									setIsPressed={setFilter}
								/>
							</Box>
						</Portal>

						{/* desktop filters goes here */}
						<Dialog.Root
							open={!isMobile && filter.show}
							onOpenChange={e => handleCloseFilter(e)}
						>
							<Dialog.Content
								css={{
									padding: 'unset',
									backgroundColor: '#fff',
									width: '50%',
									height: 465,
									borderRadius: 12,
								}}
							>
								<Flex
									css={{
										height: 64,
										width: '100%',
										borderBottomWidth: 1,
										borderBottomStyle: 'solid',
										borderBottomColor: '#F0F0F0',
									}}
								>
									<Row
										css={{
											width: '100%',
											alignItems: 'center',
											paddingLeft: 32,
										}}
									>
										<Text
											css={{
												margin: 0,
												fontSize: 22,
												fontWeight: 600,
												color: '#330000',
											}}
										>
											All filters
										</Text>
									</Row>
								</Flex>

								<Row
									css={{
										width: '100%',
										flexWrap: 'wrap',
										gap: 56,
										alignItems: 'baseline',
										overflow: 'scroll',
										padding: '32px',
									}}
								>
									{filters?.map((val, inx) => (
										<Column key={'filter' + inx}>
											<CustomCheckbox
												key={'checkbox' + inx}
												groupKey={val.key}
												heading={val.name}
												data={val.options.map(value => ({
													...value,
													checked: checkCondition({
														key: val.key,
														value: value.key,
													})
														? true
														: false,
													icon: checkCondition({
														key: val.key,
														value: value.key,
													}) ? (
														<CheckBoxActive fill="#00774F" />
													) : (
														<CheckBoxBlank />
													),
												}))}
												onClick={e => {
													onCheckboxClick(e)
													trackEvent(EVENT_Filter_CLICKED, {
														Catagory: e?.groupKey,
														Filter: e?.key,
													})
												}}
											/>
										</Column>
									))}
								</Row>

								<Column
									css={{
										marginTop: 12,
										justifyContent: 'center',
										width: '100%',
										height: 72,
										boxShadow: '0px 2px 15px rgba(31, 38, 61, 0.11)',
										paddingLeft: 36,
										paddingRight: 36,
										borderBottomLeftRadius: 12,
										borderBottomRightRadius: 12,
									}}
								>
									<Row
										css={{
											gap: 16,
										}}
									>
										<Button
											primary
											css={{
												width: 145,
												height: 40,
												fontWeight: 700,
											}}
											onClick={handleFilter}
										>
											Apply Filters
										</Button>
										<Button
											outlined
											css={{
												width: 145,
												height: 40,
												border: 'none',
												// color: '#000000E5',
												fontWeight: 600,
											}}
											onClick={clearAllFilters}
										>
											Clear all
										</Button>
									</Row>
								</Column>
							</Dialog.Content>
						</Dialog.Root>
					</ContentWrapper>

					{/* mobile filters goes here */}
					<Box
						css={{
							'@lg': {
								display: 'none',
							},
						}}
					>
						<Dialog.Root
							open={filter.show && isMobile}
							onOpenChange={e => handleCloseFilter(e)}
						>
							<Dialog.Content
								css={{
									width: '100%',
									backgroundColor: '#fff',
									// height: 500,
									borderTopWidth: 0,
									borderTopLeftRadius: 12,
									borderTopRightRadius: 12,
									top: 'unset',
									left: 'unset',
									transform: 'translate(0, 0)',
									marginTop: 0,
									bottom: 0,
									paddingBottom: '104px',
								}}
							>
								{mobileFilterArray?.map((val, inx) => (
									<Column key={'filter' + inx}>
										<Row
											css={{
												width: '100%',
												paddingTop: 12,
												paddingBottom: 12,
												alignItems: 'baseline',
												borderBottomWidth: 1,
												borderBottomStyle: 'solid',
												borderBottomColor: '#F0F0F0',
											}}
										>
											<Text
												css={{
													marginLeft: 12,
													fontSize: 18,
													fontWeight: 600,
													color: '#333',
												}}
											>
												{filter.key === 'hotelRatings'
													? starFilter[0]?.name
													: filter.key === 'durations'
													? durationFilter[0]?.name
													: filter.key === 'budgets'
													? budgetFilter[0]?.name
													: []}
											</Text>
										</Row>
										<Column
											css={{
												marginLeft: 20,
												marginRight: 20,
											}}
										>
											<CustomCheckbox
												key={'checkbox' + inx}
												groupKey={val.key}
												data={val.options.map(value => ({
													...value,
													checked: checkCondition({
														key: val.key,
														value: value.key,
													})
														? true
														: false,
													icon: checkCondition({
														key: val.key,
														value: value.key,
													}) ? (
														<CheckBoxActive fill="#00774F" />
													) : (
														<CheckBoxBlank />
													),
												}))}
												onClick={e => onCheckboxClick(e)}
											/>
										</Column>
									</Column>
								))}
								<Column
									css={{
										width: '100%',
										height: 56,
										position: 'fixed',
										bottom: 0,
										left: 0,
										boxShadow: '0px 2px 15px rgba(31, 38, 61, 0.11)',
									}}
								>
									<Row
										css={{
											marginTop: 'auto',
											marginBottom: 'auto',
											paddingLeft: 20,
											paddingRight: 20,
											justifyContent: 'space-between',
											alignItems: 'center',
										}}
									>
										<Box onClick={clearThisFilter}>
											<Text
												css={{
													fontSize: 15,
													fontWeight: 600,
													textDecoration: 'underline',
													color: 'rgba(0, 0, 0, 0.9)',
												}}
											>
												Clear all
											</Text>
										</Box>
										<Button
											onClick={handleFilter}
											primary
											css={{
												width: 156,
												height: 40,
												fontWeight: 700,
											}}
										>
											Apply filters
										</Button>
									</Row>
								</Column>
							</Dialog.Content>
						</Dialog.Root>
					</Box>
				</Box>

				{/* // Modals */}
				{isAmp ? null : <GCMContainer />}
				<CbrWrapper regionCode={regionCode} />
				{isMobile ? (
					<Drawer.Root open={showCBR} onOpenChange={e => setShowCBR(e)}>
						<Drawer.Content
							side="bottom"
							css={{
								backgroundColor: '#fff',
								height: '100%',
							}}
						>
							<TalkToExpert
								showClose={false}
								vacationFlow={showLeadGenForm}
								ctaText="Get best quotes"
								title={pageTitleCap}
								regionCode={regionCode}
								isMobile={isMobile}
								formVisible={showCBR}
								scrollToPackagesList={scrollToPackages}
								callback={
									selectedItinerary?.itinerary ? showGetCostModal : null
								}
								locales={locale}
								closeModal={onCloseModal}
							/>
						</Drawer.Content>
					</Drawer.Root>
				) : (
					<Dialog.Root open={showCBR} onOpenChange={e => setShowCBR(e)}>
						<Dialog.Content
							css={{
								padding: '20px 0px',
								// height: 'calc(100vh - 450px)',
								width: '40%',
								minHeight: '250px',
								display: 'flex',
								alignItems: 'center',
							}}
						>
							<TalkToExpert
								showClose={false}
								vacationFlow={showLeadGenForm}
								ctaText="Get best quotes"
								title={pageTitleCap}
								regionCode={regionCode}
								isMobile={isMobile}
								formVisible={showCBR}
								scrollToPackagesList={scrollToPackages}
								callback={
									selectedItinerary?.itinerary ? showGetCostModal : null
								}
								locales={locale}
								closeModal={onCloseModal}
							/>
						</Dialog.Content>
					</Dialog.Root>
				)}
			</Box>

			{showSbCards ? (
				<CBRFloatingBar
					isMobile={isMobile}
					wrapperCss={{ bottom: showSbCards ? '32px' : '70px' }}
				/>
			) : !isPaid ? (
				<FloatingCTA
					show={scrollPosition && !readMoreOpened}
					onCtaClick={() => {
						trackEvent(EVENT_CREATE_NOW_CLICKED)
						if (regionCode) {
							if (regionCode === 'mle') {
								router.push('/maldives-tour-packages')
							} else {
								redirector(`/customize/${regionCode}`)
							}
						} else {
							redirector('/customize')
						}
					}}
					text={
						isMobile
							? 'Build Your Itinerary Yourself'
							: 'Customise and build your own itinerary'
					}
					ctaText="Create Now"
					bottomPosition={70}
				/>
			) : null}
		</Fragment>
	)
}

export default VacationsListingPage
